import {computed, Injectable, Input, OnInit, signal, WritableSignal} from '@angular/core';
import {ProyectoService} from "./proyecto.service";
import {ProyectoItem, ProyectoTramiteItem} from "../../interfaces/proyecto.interface";
import {LoginServices} from "../../../auth/login/services/login.service";
import {Param} from "../../../shared/interfaces/param-interface";
import {ProyectoAsignado} from "../../../officials/asignaciones/interfaces/asignacion-interface";
import {HttpClient} from "@angular/common/http";
import {FiltersService} from "../../../shared/services/filters-service";
import {convertDate} from "../../../shared/services/utils-service";
import {TramiteEstadoEnum} from "../components/ver-proyecto/interface/ver-proyecto.interface";


@Injectable({
    providedIn: 'root'
})
export class ProyectoDataService implements OnInit {
    private _itemsPivot: WritableSignal<ProyectoItem[]> = signal([])
    private _itemsPorEstado: WritableSignal<ProyectoItem[]> = signal([])
    private _itemsTipoRegProf: WritableSignal<ProyectoItem[]> = signal([])
    private _itemsPorEmpleado: WritableSignal<ProyectoAsignado[]> = signal([])
    private _itemsResumenPorUsuario: WritableSignal<ProyectoAsignado[]> = signal([])
    private _tramitesPorProyecto: WritableSignal<ProyectoTramiteItem[]> = signal([]);
    private readonly _loading = signal(false);
    readonly loading = this._loading.asReadonly();
    /**
     * Estado de solicitud seleccionado en pantalla. Por defecto 1=Pendiente
     */
    @Input()
    selectedId: string | undefined = '1';
    /**
     * Título del Estado de solicitud seleccionado en pantalla. Por defecto 1=Pendiente
     */
    @Input()
    selectedTitle: string | undefined = '';

    constructor(private loginServices: LoginServices, private proyectoService: ProyectoService, private http: HttpClient, private filtersService: FiltersService) {
        this._itemsPorEstado.set([])
        this._itemsTipoRegProf.set([])
    }

    ngOnInit(): void {
    }

    /**
     * Esta consulta filtra por 3 parametros: usuario en sesion, estado de asignacion y estado de solicitud.
     */
    consultarAsignadosPorUsuarioSession() {
        this._itemsPorEstado.set([])
        this._loading.set(true);
        const params: Param[] = [{
            key: "usuarios_asig", value: this.loginServices.session()?.perfil.usuario
        }, {key: "estados_asig", value: "1"}, {key: "estados_solicitud", value: this.selectedId}
        ];
        let drange = this.filtersService.selectedRange;
        let desde;
        let hasta;
        if (drange != null) {
            desde = convertDate(drange[0]);
            hasta = convertDate(drange[1]);
            params.push({key: "desde", value: desde});
            params.push({key: "hasta", value: hasta});
        }
        console.log(`params: ${JSON.stringify(params)}`);
        return this.proyectoService.consultarPorParametros(params).subscribe({
            next: (result) => {
                if (result && result.body) {
                    this._itemsPorEstado.set(result.body)
                }
            },
            error: (error) => {
                console.log(error)
                this._loading.set(false);
            },
            complete: () => {
                this._loading.set(false);
            }
        });
    }

    /**
     * Consulta por rango de fechas y tipo de solicitud.
     * Si el componente de rango de fechas está presente en pantalla, automáticamente aplica el filtro de las fechas.
     *
     * <p>
     *      idTipoSolicitud=1 -->Solicitud Integrada
     *      idTipoSolicitud=2 -->Solicitud Individual
     *      idTipoSolicitud=3 --> Solicitud Registro de profesionales
     *</p>
     */
    consultarPorTipoDeSolicitud(filtrarPorTipoSolicitud ?: string, usuarioLogueado: boolean = false) {
        this._itemsPorEstado.set([])
        this._loading.set(true);
        const params: Param[] = [{key: "estados_solicitud", value: this.selectedId}];
        let drange = this.filtersService.selectedRange;
        let desde;
        let hasta;
        if (drange != null) {
            desde = convertDate(drange[0]);
            hasta = convertDate(drange[1]);
            params.push({key: "desde", value: desde});
            params.push({key: "hasta", value: hasta});
            if (filtrarPorTipoSolicitud) {
                params.push({key: "tipo", value: filtrarPorTipoSolicitud});
            }
        }
        if (usuarioLogueado) {
            if (this.loginServices.isRolCliente() || this.loginServices.isRolGerenteTecnico()) {
                params.push({key: "usuarios", value: this.loginServices.session()?.perfil.usuario});
            } else {
                params.push({key: "usuarios_asig", value: this.loginServices.session()?.perfil.usuario});
            }
        }
        return this.proyectoService.consultarPorParametros(params).subscribe({
            next: (result) => {
                if (result && result.body) {
                    this._itemsPorEstado.set(result.body);
                    this._itemsPivot.set(result.body);
                }
            },
            error: (error) => {
                console.log(error)
                this._loading.set(false);
            },
            complete: () => {
                this._loading.set(false);
            }
        });
    }

    get itemsTipoRegProf(): WritableSignal<ProyectoItem[]> {
        return this._itemsTipoRegProf;
    }

    set itemsTipoRegProf(value: WritableSignal<ProyectoItem[]>) {
        this._itemsTipoRegProf = value;
    }

    get itemsPorEstado(): WritableSignal<ProyectoItem[]> {
        return this._itemsPorEstado;
    }

    set itemsPorEstado(value: WritableSignal<ProyectoItem[]>) {
        this._itemsPorEstado = value;
    }

    get itemsPorEmpleado(): WritableSignal<ProyectoAsignado[]> {
        return this._itemsPorEmpleado;
    }

    set itemsPorEmpleado(value: WritableSignal<ProyectoAsignado[]>) {
        this._itemsPorEmpleado = value;
    }

    get itemsResumenPorUsuario(): WritableSignal<ProyectoAsignado[]> {
        return this._itemsResumenPorUsuario;
    }

    set itemsResumenPorUsuario(value: WritableSignal<ProyectoAsignado[]>) {
        this._itemsResumenPorUsuario = value;
    }

    get tramitesPorProyecto(): WritableSignal<ProyectoTramiteItem[]> {
        return this._tramitesPorProyecto;
    }

    set tramitesPorProyecto(value: WritableSignal<ProyectoTramiteItem[]>) {
        this._tramitesPorProyecto = value;
    }

    get itemsPivot(): WritableSignal<ProyectoItem[]> {
        return this._itemsPivot;
    }

    set itemsPivot(value: WritableSignal<ProyectoItem[]>) {
        this._itemsPivot = value;
    }

    valitedButtonTemplate = computed(() => {
        if (!this.tramitesPorProyecto()) {
            return false;
        }
        return this.countTramitesPorEstado(this.tramitesPorProyecto(), TramiteEstadoEnum.PagoRealizado);
    });

    countTramitesPorEstado(items: ProyectoTramiteItem[], codigoEstados: number) {
        if (!items || items.length === 0) {
            return false;
        }
        return items.some(item => item.tramiteEstado?.codigoEstado !== codigoEstados);
    }


    /**
     * Metodo para proyectos inspecciones para gerencia tecnica*/
    //TODO: consulta con gerencia tecnica que estados son validos para realizar inspecciones
    consultarAsignadosPorUsuarioQ1SessionInspecciones() {
        this._itemsPorEstado.set([])
        this._loading.set(true);
        const params: Param[] = [{key: "estados_solicitud", value: this.selectedId}, {key: "tipo", value: "1,2"}
        ];
        let drange = this.filtersService.selectedRange;
        let desde;
        let hasta;
        if (drange != null) {
            desde = convertDate(drange[0]);
            hasta = convertDate(drange[1]);
            params.push({key: "desde", value: desde});
            params.push({key: "hasta", value: hasta});
        }
        console.log(`params: ${JSON.stringify(params)}`);
        return this.proyectoService.consultarPorParametros(params).subscribe({
            next: (result) => {
                if (result && result.body) {
                    this._itemsPorEstado.set(result.body)
                }
            },
            error: (error) => {
                console.log(error)
                this._loading.set(false);
            },
            complete: () => {
                this._loading.set(false);
            }
        });
    }

    /**
     * Metodo para proyectos inspecciones para tecnicos*/
    consultarAsignadosPorUsuarioQ2SessionInspecciones() {
        this._itemsPorEstado.set([])
        this._loading.set(true);
        const params: Param[] = [{
            key: "usuarios_asig", value: this.loginServices.session()?.perfil.usuario},
            {key: "estados_asig", value: "1"}, //estado de tabla proyecto asignacion
            {key: "estados_solicitud", value: '1,2,3,4,6,11'},
            {key: "tipo", value: "1,2"}
        ];
        let drange = this.filtersService.selectedRange;
        let desde;
        let hasta;
        if (drange != null) {
            desde = convertDate(drange[0]);
            hasta = convertDate(drange[1]);
            params.push({key: "desde", value: desde});
            params.push({key: "hasta", value: hasta});
        }
        console.log(`params: ${JSON.stringify(params)}`);
        return this.proyectoService.consultarPorParametros(params).subscribe({
            next: (result) => {
                if (result && result.body) {
                    this._itemsPorEstado.set(result.body)
                }
            },
            error: (error) => {
                console.log(error)
                this._loading.set(false);
            },
            complete: () => {
                this._loading.set(false);
            }
        });
    }
}
